  import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { AuthPageShell, TariffList } from '@/components';
import { ChevronDownIcon } from '@/components/icons.tsx';
import { useAuthFetch, usePractice } from '@/hooks';
import { ROUTES, practiceConversationPath, practiceIntakePath } from '../../routes.ts';
import type { ConversationSummary } from '@dermalink/shared';
import logoIconWhite from '../../assets/logo-icon-white.svg';
import { getInitialsFromName, inactiveClosesOnLabel } from '@/utils';
import styles from './PatientDashboard.module.css';

export function PatientDashboard() {
  const navigate  = useNavigate();
  const authFetch = useAuthFetch();
  const { slug: practiceSlug, practice } = usePractice();

  const { data, isPending, isError } = useQuery({
    queryKey: ['patient-home'],
    queryFn: async () => {
      const [convRes, profileRes] = await Promise.all([
        authFetch('/conversations'),
        authFetch('/profile'),
      ]);
      if (!convRes.ok) throw new Error('Failed to load conversations');
      const conversations: ConversationSummary[] = await convRes.json();
      const profile = profileRes.ok
        ? (await profileRes.json()) as { first_name?: string | null; last_name?: string | null }
        : null;
      const firstName = profile?.first_name?.trim() ?? '';
      const lastInitial = profile?.last_name?.trim()?.[0]?.toUpperCase() ?? '';
      const patientDisplayName = firstName
        ? `${firstName}${lastInitial ? ` ${lastInitial}.` : ''}`
        : '';
      return { conversations, patientDisplayName };
    },
    refetchInterval: 10_000,
  });

  const patientDisplayName = data?.patientDisplayName ?? '';
  const conversations = (data?.conversations ?? []).filter((conv) => conv.doctor_organisation_slug === practiceSlug);
  const conversationCount = conversations.length;

  function formatDateLabel(iso: string): string {
    const date = new Date(iso);
    const now = new Date();
    const isToday =
      date.getFullYear() === now.getFullYear()
      && date.getMonth() === now.getMonth()
      && date.getDate() === now.getDate();
    if (isToday) return 'Vandaag';
    return date.toLocaleDateString('nl-BE', { day: 'numeric', month: 'short' });
  }

  function subjectLine(conv: ConversationSummary): string {
    if (!conv.subject_first_name) return 'Voor mezelf';
    return `Voor ${conv.subject_first_name} ${conv.subject_last_name}`;
  }

  function statusText(conv: ConversationSummary): string {
    if (conv.flagged_inactive_at && conv.status !== 'closed') return `Verloopt ${inactiveClosesOnLabel(conv.flagged_inactive_at)}`;
    const status = conv.status;
    if (status === 'closed') return 'Afgerond';
    if (status === 'active') return 'Actief';
    if (status === 'resolution_rejected') return 'Actief';
    if (status === 'pending_payment') return 'Betaling vereist';
    if (status === 'payment_requested') return 'Betaling gevraagd';
    if (status === 'closure_requested') return 'Consultatie afronden';
    return 'AFGEROND';
  }

  function statusClass(conv: ConversationSummary): string {
    if (conv.flagged_inactive_at && conv.status !== 'closed') return styles.statusWarning;
    const status = conv.status;
    if (status === 'closed') return styles.statusDone;
    if (status === 'active') return styles.statusActive;
    if (status === 'resolution_rejected') return styles.statusActive;
    if (status === 'pending_payment') return styles.statusPending;
    if (status === 'payment_requested') return styles.statusPending;
    if (status === 'closure_requested') return styles.statusPending;
    return styles.statusDone;
  }

  if (practice === null) {
    return (
      <AuthPageShell maxWidth="720px" padTop="16px" patientBackdrop>
        <p className="status-message" role="alert">
          Deze praktijk werd niet gevonden. <Link to={ROUTES.practices}>Kies een praktijk</Link>.
        </p>
      </AuthPageShell>
    );
  }

  return (
    <AuthPageShell maxWidth="720px" padTop="16px" patientBackdrop practice={practice}>
      <section className={styles.heroCard}>
        <p className={styles.welcomeLabel}>WELKOM TERUG</p>
        <h1 className={styles.welcomeTitle}>
          Hallo <span className={styles.personName}>{patientDisplayName || 'daar'}</span>
        </h1>
        <p className={styles.welcomeText}>
          We streven ernaar om uw vragen zo snel mogelijk te beantwoorden.
          Voor problemen die dringend behandeld moeten worden neemt u best rechtstreeks telefonisch contact op met de praktijk of de huisarts.
        </p>

        <button
          type="button"
          className={styles.ctaButton}
          onClick={() => navigate(practiceIntakePath(practiceSlug))}
        >
          <img src={logoIconWhite} alt="" aria-hidden="true" className={styles.ctaIcon} />
          <span className={styles.ctaTextWrap}>
            <span className={styles.ctaTitle}>Nieuwe teleconsultatie starten</span>
            {practice && <span className={styles.ctaSub}>bij <strong>{practice.name}</strong></span>}
          </span>
          <span className={styles.ctaArrow} aria-hidden="true">›</span>
        </button>

        <details className={styles.priceAccordion}>
          <summary className={styles.priceAccordionSummary}>
            <span>Hoe is de prijs opgebouwd?</span>
            <ChevronDownIcon size={12} className={styles.priceAccordionChevron} />
          </summary>
          <div className={styles.priceAccordionBody}>
            <TariffList />
          </div>
        </details>
      </section>

      <section className={styles.listSection}>
        <div className={styles.listHeader}>
          <span className={styles.listTitle}>EERDERE CONSULTEN</span>
          <span className={styles.listCount}>{conversationCount} totaal</span>
        </div>


        {isPending && <p className={styles.infoMessage}>Laden...</p>}
        {isError && <p className={styles.infoMessage}>Kon gesprekken niet laden.</p>}

        {!isPending && !isError && conversationCount === 0 && (
          <>
            <p className={styles.infoMessage}>
              {practice ? `Nog geen eerdere consulten bij ${practice.name}.` : 'Nog geen eerdere consulten.'}
            </p>
          </>
        )}

        

        {conversations.map((conv) => {
          const portrait = conv.doctor_picture_url;
          return (
          <button
            key={conv.id}
            type="button"
            className={styles.consultRow}
            onClick={() => navigate(practiceConversationPath(practiceSlug, conv.id))}
          >
            <span className={styles.avatar} aria-hidden="true">
              {portrait
                ? <img src={portrait} alt="" className={styles.avatarImage} />
                : getInitialsFromName(conv.doctor_name, 'D')}
            </span>
            <span className={styles.rowMain}>
              <span className={styles.doctorName}>Dr. {conv.doctor_name ?? 'Onbekend'}</span>
              <span className={styles.subjectLine}>{subjectLine(conv)}</span>
              <span className={`${styles.statusPill} ${statusClass(conv)}`}>
                {statusText(conv)}
              </span>
            </span>
            <span className={styles.rowMeta}>
              <span className={styles.dateLabel}>{formatDateLabel(conv.last_message_at ?? conv.created_at)}</span>
              {conv.unread_count > 0 && (
                <span className={styles.unreadBadge} aria-label={`${conv.unread_count} ongelezen berichten`}>
                  {conv.unread_count > 99 ? '99+' : conv.unread_count}
                </span>
              )}
              <span className={styles.rowArrow} aria-hidden="true">›</span>
            </span>
          </button>
          );
        })}
      </section>
    </AuthPageShell>
  );
}
